import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  alpha,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
} from 'recharts';
import { TIER_META } from '../constants';
import {
  getInvestorGuidanceText,
  getLocalizedBreakoutLabel,
  isPrepBuySignal,
  regimeColor,
  signalColor,
  triggerColor,
} from '../utils/labels';
import { PrepDrawerDetailSkeleton, PrepSk } from './PrepSkeletons';

export default function PreparationDetailDrawer({
  t,
  i18n,
  detailDrawer,
  setDetailDrawer,
  detailHistory,
  drawerExportRef,
  drawerBodyRef,
  exportingDrawer,
  exportingChart,
  chartExportRef,
  onExportDrawer,
  onExportChart,
  fmtNum,
  fmtPct,
  fmtPercentUnits,
  fmtMultiplier,
  getTriggerLabel,
  getSectorLabel,
  localizeReason,
  getTier,
  getPillarVerdict,
}) {
  return (
        <Drawer
          anchor="right"
          open={!!detailDrawer}
          onClose={() => setDetailDrawer(null)}
          PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, p: 0 } }}
        >
          <Box ref={drawerExportRef} sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#fff' }}>
          <Box sx={{
            position: 'relative',
            px: 2.25,
            py: 1.6,
            borderBottom: '1px solid #e2e8f0',
            bgcolor: '#fff',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Box sx={{
                width: 46,
                height: 46,
                borderRadius: 1,
                bgcolor: '#0f172a',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                overflow: 'hidden',
              }}>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: '#fff', lineHeight: 1, letterSpacing: '0.02em' }}>
                  {detailDrawer?.ticker || '—'}
                </Typography>
                <Box sx={{ mt: 0.45, width: '100%', bgcolor: '#7c3aed', textAlign: 'center', py: 0.15 }}>
                  <Typography sx={{ fontSize: '0.5rem', fontWeight: 800, color: '#fff', letterSpacing: '0.14em' }}>
                    PREP
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ minWidth: 0, flex: 1 }}>
                {detailDrawer?.company_name && (
                  <Typography sx={{ fontSize: '0.82rem', color: '#0f172a', fontWeight: 700, lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {detailDrawer.company_name}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, mt: 0.5, flexWrap: 'wrap' }}>
                  {detailDrawer?.signal_label && (
                    <Box sx={{
                      px: 0.7,
                      py: 0.15,
                      borderRadius: 0.6,
                      bgcolor: alpha(signalColor(detailDrawer.signal), 0.12),
                      border: `1px solid ${alpha(signalColor(detailDrawer.signal), 0.35)}`,
                      fontSize: '0.6rem',
                      fontWeight: 800,
                      color: signalColor(detailDrawer.signal),
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      lineHeight: 1.2,
                    }}>
                      {getLocalizedBreakoutLabel(detailDrawer.signal_label, i18n.resolvedLanguage)}
                    </Box>
                  )}
                  <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 500 }}>
                    {detailDrawer?.score_date ? (() => {
                      const [y, m, d] = detailDrawer.score_date.split('-');
                      return `${d}/${m}/${y}`;
                    })() : (detailDrawer?.type === 'history' ? t('preparation.detail.historyMode') : t('preparation.detail.liveMode'))}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, alignSelf: 'center' }} data-html2canvas-ignore="true">
                <Tooltip title={t('preparation.detail.exportDrawerImage')} placement="top">
                  <span>
                    <IconButton
                      size="small"
                      onClick={onExportDrawer}
                      disabled={exportingDrawer}
                      sx={{ color: '#64748b', '&:hover': { color: '#0f172a', bgcolor: '#f1f5f9' } }}
                    >
                      {exportingDrawer ? <CircularProgress size={14} color="inherit" /> : <DownloadRoundedIcon sx={{ fontSize: 18 }} />}
                    </IconButton>
                  </span>
                </Tooltip>
                <IconButton size="small" onClick={() => setDetailDrawer(null)} sx={{ color: '#64748b' }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </Box>

          <Box ref={drawerBodyRef} sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
            {detailDrawer?.loadingDetail ? (
              <PrepDrawerDetailSkeleton />
            ) : detailDrawer && (
              <Stack spacing={1.5}>
                {detailDrawer.investor_guidance ? (
                  <Box sx={{
                    p: 1.25,
                    border: '1px solid',
                    borderColor: detailDrawer.signal === 'BUY_CAUTION' ? alpha('#b45309', 0.35) : alpha('#16a34a', 0.35),
                    borderRadius: 1.25,
                    bgcolor: detailDrawer.signal === 'BUY_CAUTION' ? alpha('#b45309', 0.06) : alpha('#16a34a', 0.06),
                  }}>
                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
                      {t('preparation.detail.investorGuidanceTitle')}
                    </Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: '#334155', lineHeight: 1.45, fontWeight: 600 }}>
                      {getInvestorGuidanceText(detailDrawer.investor_guidance, i18n.resolvedLanguage)}
                    </Typography>
                    {detailDrawer.investor_guidance.route_path && detailDrawer.investor_guidance.route_path !== '/preparation-phase' ? (
                      <Button
                        component={RouterLink}
                        to={detailDrawer.investor_guidance.route_path}
                        size="small"
                        sx={{ mt: 1, fontSize: '0.68rem', fontWeight: 800, textTransform: 'none' }}
                      >
                        {t('preparation.detail.openBreakoutScanner')}
                      </Button>
                    ) : null}
                  </Box>
                ) : null}
                <Box sx={{ p: 1.5, border: '1px solid #e2e8f0', borderRadius: 1.25, bgcolor: '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                    <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      {t('preparation.detail.overview')}
                    </Typography>
                    <Box component="a" href={`/stock-detail/${detailDrawer.ticker}`} sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#2563eb', textDecoration: 'none' }}>
                      {t('preparation.detail.openChart')}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
                    <Box>
                      <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>{t('preparation.table.headers.score')}</Typography>
                      {(() => {
                        const tier = getTier(detailDrawer.score);
                        const meta = tier ? TIER_META[tier] : null;
                        return (
                          <Typography sx={{ fontSize: '0.92rem', fontWeight: 800, color: meta ? meta.color : '#0f172a', lineHeight: 1.15 }}>
                            {tier ? t(`preparation.detail.scoreTier.${tier}`) : '—'}
                          </Typography>
                        );
                      })()}
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>{t('preparation.table.headers.signal')}</Typography>
                      <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: signalColor(detailDrawer.signal) }}>{getLocalizedBreakoutLabel(detailDrawer.signal_label, i18n.resolvedLanguage)}</Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>{t('preparation.table.headers.trigger')}</Typography>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: triggerColor(detailDrawer.trigger_type) }}>{getTriggerLabel(detailDrawer.trigger_type, detailDrawer.trigger_type_label)}</Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>{t('preparation.table.headers.sector')}</Typography>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#334155' }}>{getSectorLabel(detailDrawer.sector)}</Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>{t('preparation.detail.marketRegime')}</Typography>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: regimeColor(detailDrawer.market_regime) }}>{getLocalizedBreakoutLabel(detailDrawer.market_regime_label, i18n.resolvedLanguage)}</Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>{t('preparation.table.headers.close')}</Typography>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#0f172a' }}>{detailDrawer.price?.close != null ? fmtNum(detailDrawer.price.close, 0) : '—'}</Typography>
                    </Box>
                  </Box>
                </Box>

                {(detailDrawer.type === 'live' || detailDrawer.type === 'history') && (
                  <>
                    <Box sx={{ p: 1.5, border: '1px solid #e2e8f0', borderRadius: 1.25, bgcolor: '#fff' }}>
                      <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 }}>
                        {t('preparation.detail.entryContext')}
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
                        <Box>
                          <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>{t('preparation.table.headers.distResistance')}</Typography>
                          <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: detailDrawer.price?.dist_from_resistance < 0 ? '#0369a1' : '#dc2626' }}>{detailDrawer.price?.dist_from_resistance != null ? fmtPct(detailDrawer.price.dist_from_resistance) : '—'}</Typography>
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>{t('preparation.table.headers.upDown')}</Typography>
                          <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#334155' }}>{detailDrawer.smart_money?.up_value_ratio != null ? fmtMultiplier(detailDrawer.smart_money.up_value_ratio, 2) : '—'}</Typography>
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>{t('preparation.table.headers.accumulation')}</Typography>
                          <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#334155' }}>{detailDrawer.smart_money?.accumulation_days_10 != null ? t('preparation.table.accumulationValue', { value: detailDrawer.smart_money.accumulation_days_10 }) : '—'}</Typography>
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>{t('preparation.detail.supportTests')}</Typography>
                          <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#334155' }}>{detailDrawer.smart_money?.support_hold_count ?? '—'}</Typography>
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>{t('preparation.detail.resistance20d')}</Typography>
                          <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#334155' }}>{detailDrawer.price?.resistance_20d != null ? fmtNum(detailDrawer.price.resistance_20d, 0) : '—'}</Typography>
                        </Box>
                      </Box>
                    </Box>

                    {detailDrawer.trade_plan ? (
                      <Box sx={{ p: 1.5, border: '1px solid #e2e8f0', borderRadius: 1.25, bgcolor: '#fff' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.1 }}>
                          <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            {t('preparation.detail.tradePlanTitle')}
                          </Typography>
                          {detailDrawer.trade_plan.plan_profile_label && (
                            <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color: '#1d4ed8' }}>
                              {getLocalizedBreakoutLabel(detailDrawer.trade_plan.plan_profile_label, i18n.resolvedLanguage)}
                            </Typography>
                          )}
                        </Box>

                        <Stack spacing={0.85}>
                          {/* Entry */}
                          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.1 }}>
                            <Box sx={{ width: 3, alignSelf: 'stretch', borderRadius: 999, bgcolor: '#0ea5e9' }} />
                            <Typography sx={{ flex: '0 0 86px', fontSize: '0.66rem', color: '#0369a1', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                              {t('preparation.detail.entryZone')}
                            </Typography>
                            <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
                              {detailDrawer.trade_plan.entry_low != null && detailDrawer.trade_plan.entry_high != null
                                ? `${fmtNum(detailDrawer.trade_plan.entry_low, 0)} – ${fmtNum(detailDrawer.trade_plan.entry_high, 0)}`
                                : '—'}
                            </Typography>
                          </Box>

                          {/* Stop */}
                          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.1 }}>
                            <Box sx={{ width: 3, alignSelf: 'stretch', borderRadius: 999, bgcolor: '#dc2626' }} />
                            <Typography sx={{ flex: '0 0 86px', fontSize: '0.66rem', color: '#b91c1c', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                              {t('preparation.detail.stopLoss')}
                            </Typography>
                            <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
                              {detailDrawer.trade_plan.stop != null ? fmtNum(detailDrawer.trade_plan.stop, 0) : '—'}
                            </Typography>
                            {detailDrawer.trade_plan.risk_pct != null && (
                              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#dc2626', ml: 'auto' }}>
                                −{fmtPercentUnits(detailDrawer.trade_plan.risk_pct * 100, 1)}
                              </Typography>
                            )}
                          </Box>

                          {/* Target */}
                          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.1 }}>
                            <Box sx={{ width: 3, alignSelf: 'stretch', borderRadius: 999, bgcolor: '#16a34a' }} />
                            <Typography sx={{ flex: '0 0 86px', fontSize: '0.66rem', color: '#15803d', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                              {t('preparation.detail.target')}
                            </Typography>
                            <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
                              {detailDrawer.trade_plan.target != null ? fmtNum(detailDrawer.trade_plan.target, 0) : '—'}
                            </Typography>
                            {detailDrawer.trade_plan.reward_pct != null && (
                              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#16a34a', ml: 'auto' }}>
                                +{fmtPercentUnits(detailDrawer.trade_plan.reward_pct * 100, 1)}
                              </Typography>
                            )}
                          </Box>
                        </Stack>

                        <Divider sx={{ my: 1.1 }} />

                        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
                          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.6 }}>
                            <Typography sx={{ fontSize: '0.66rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                              {t('preparation.detail.rr')}
                            </Typography>
                            <Typography sx={{ fontSize: '0.86rem', fontWeight: 800, color: detailDrawer.trade_plan.rr >= 2 ? '#16a34a' : detailDrawer.trade_plan.rr >= 1.5 ? '#0369a1' : '#b45309' }}>
                              {detailDrawer.trade_plan.rr != null ? `${fmtNum(detailDrawer.trade_plan.rr, 1)}x` : '—'}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.6 }}>
                            <Typography sx={{ fontSize: '0.66rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                              {t('preparation.detail.positionSize')}
                            </Typography>
                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#0f172a' }}>
                              {detailDrawer.trade_plan.position_size_pct != null ? t('preparation.detail.positionSizeValue', { value: fmtNum(detailDrawer.trade_plan.position_size_pct, 0) }) : '—'}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ p: 1.25, border: '1px dashed #cbd5e1', borderRadius: 1.25, bgcolor: '#f8fafc' }}>
                        <Typography sx={{ fontSize: '0.72rem', color: '#64748b', fontStyle: 'italic' }}>
                          {t('preparation.detail.noTradePlan')}
                        </Typography>
                      </Box>
                    )}

                    {/* Signal history chart */}
                    <Box ref={chartExportRef} sx={{ p: 1.5, border: '1px solid #e2e8f0', borderRadius: 1.25, bgcolor: '#fff' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.8, gap: 1 }}>
                        <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          {t('preparation.detail.signalHistoryTitle')}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8' }}>
                            {t('preparation.detail.signalHistorySub', { count: detailHistory.data.length })}
                          </Typography>
                          <Tooltip title={t('preparation.detail.exportChartImage')} placement="top">
                            <span data-html2canvas-ignore="true">
                              <IconButton
                                size="small"
                                onClick={onExportChart}
                                disabled={exportingChart || detailHistory.loading || detailHistory.data.length === 0}
                                sx={{ p: 0.4, color: '#64748b', '&:hover': { color: '#0f172a', bgcolor: '#f1f5f9' } }}
                              >
                                {exportingChart ? <CircularProgress size={14} color="inherit" /> : <DownloadRoundedIcon sx={{ fontSize: 16 }} />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </Box>

                      {detailHistory.loading ? (
                        <>
                          <PrepSk variant="rectangular" width="100%" height={150} sx={{ borderRadius: 1 }} />
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, mt: 0.6, flexWrap: 'wrap' }}>
                            {[48, 56, 44, 40].map((w) => (
                              <PrepSk key={w} width={w} height={10} />
                            ))}
                          </Box>
                        </>
                      ) : detailHistory.data.length === 0 ? (
                        <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', fontStyle: 'italic', py: 2, textAlign: 'center' }}>
                          {t('preparation.detail.signalHistoryEmpty')}
                        </Typography>
                      ) : (
                        <>
                          <Box sx={{ width: '100%', height: 150 }}>
                            <ResponsiveContainer>
                              <ComposedChart data={detailHistory.data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                                <XAxis
                                  dataKey="score_date"
                                  tick={{ fontSize: 9, fill: '#94a3b8' }}
                                  tickFormatter={(d) => d ? d.slice(5) : ''}
                                  interval="preserveStartEnd"
                                  axisLine={{ stroke: '#e2e8f0' }}
                                  tickLine={false}
                                />
                                <YAxis
                                  yAxisId="score"
                                  domain={[0, 100]}
                                  tick={{ fontSize: 9, fill: '#94a3b8' }}
                                  axisLine={false}
                                  tickLine={false}
                                  width={28}
                                />
                                <YAxis
                                  yAxisId="price"
                                  orientation="right"
                                  tick={{ fontSize: 9, fill: '#94a3b8' }}
                                  axisLine={false}
                                  tickLine={false}
                                  width={42}
                                  tickFormatter={(v) => v ? fmtNum(v, 0) : ''}
                                />
                                <ReferenceLine yAxisId="score" y={70} stroke="#16a34a" strokeDasharray="3 3" strokeOpacity={0.45} />
                                <ReferenceLine yAxisId="score" y={55} stroke="#f59e0b" strokeDasharray="3 3" strokeOpacity={0.35} />
                                <RTooltip
                                  contentStyle={{ fontSize: '0.7rem', borderRadius: 8, border: '1px solid #e2e8f0' }}
                                  labelStyle={{ color: '#0f172a', fontWeight: 700 }}
                                  formatter={(value, name) => {
                                    if (name === t('preparation.detail.signalHistory.score')) return [fmtNum(value, 1), name];
                                    if (name === t('preparation.detail.signalHistory.price')) return [fmtNum(value, 0), name];
                                    return [value, name];
                                  }}
                                />
                                <Bar yAxisId="score" dataKey="breakout_score" name={t('preparation.detail.signalHistory.score')} barSize={6} radius={[2, 2, 0, 0]}>
                                  {detailHistory.data.map((entry, idx) => (
                                    <Cell key={idx} fill={signalColor(entry.signal)} fillOpacity={isPrepBuySignal(entry.signal) ? (entry.signal === 'BUY_CAUTION' ? 0.85 : 0.95) : entry.signal === 'BUY_WATCH' ? 0.7 : 0.35} />
                                  ))}
                                </Bar>
                                <Line yAxisId="price" type="monotone" dataKey="close_price" name={t('preparation.detail.signalHistory.price')} stroke="#0f172a" strokeWidth={1.6} dot={false} />
                              </ComposedChart>
                            </ResponsiveContainer>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mt: 0.5, flexWrap: 'wrap' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                              <Box sx={{ width: 8, height: 8, bgcolor: signalColor('BUY_PREP'), borderRadius: 0.4 }} />
                              <Typography sx={{ fontSize: '0.62rem', color: '#64748b' }}>{t('preparation.detail.signalHistory.prepBuy')}</Typography>
                              <Box sx={{ width: 8, height: 8, bgcolor: signalColor('BUY_CAUTION'), borderRadius: 0.4 }} />
                              <Typography sx={{ fontSize: '0.62rem', color: '#64748b' }}>{t('preparation.detail.signalHistory.cautionBuy')}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                              <Box sx={{ width: 8, height: 8, bgcolor: signalColor('BUY_WATCH'), borderRadius: 0.4, opacity: 0.7 }} />
                              <Typography sx={{ fontSize: '0.62rem', color: '#64748b' }}>{t('preparation.detail.signalHistory.watch')}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                              <Box sx={{ width: 8, height: 8, bgcolor: signalColor('SKIP'), borderRadius: 0.4, opacity: 0.4 }} />
                              <Typography sx={{ fontSize: '0.62rem', color: '#64748b' }}>{t('preparation.detail.signalHistory.skip')}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                              <Box sx={{ width: 12, height: 2, bgcolor: '#0f172a' }} />
                              <Typography sx={{ fontSize: '0.62rem', color: '#64748b' }}>{t('preparation.detail.signalHistory.price')}</Typography>
                            </Box>
                          </Box>
                        </>
                      )}
                    </Box>

                    {detailDrawer.components && (
                      <Box sx={{ p: 1.5, border: '1px solid #e2e8f0', borderRadius: 1.25, bgcolor: '#fff' }}>
                        <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 }}>
                          {t('preparation.detail.pillarsTitle')}
                        </Typography>
                        <Stack spacing={1}>
                          {[
                            { key: 'market', value: detailDrawer.components.market_score },
                            { key: 'sector', value: detailDrawer.components.sector_score },
                            { key: 'quality', value: detailDrawer.components.quality_score },
                            { key: 'smartMoney', value: detailDrawer.components.smart_money_score },
                            { key: 'trigger', value: detailDrawer.components.trigger_score },
                          ].map((pillar) => {
                            const verdict = getPillarVerdict(pillar.key, pillar.value);
                            return (
                              <Box key={pillar.key}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.3, gap: 1 }}>
                                  <Typography sx={{ fontSize: '0.72rem', color: '#334155', fontWeight: 700 }}>{t(`preparation.detail.pillars.${pillar.key}`)}</Typography>
                                  {verdict.tier && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Box sx={{ px: 0.7, py: 0.1, borderRadius: 0.6, bgcolor: verdict.meta.bg, color: verdict.meta.color, fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.02em' }}>
                                        {verdict.label}
                                      </Box>
                                      <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', fontFamily: 'monospace' }}>{fmtNum(pillar.value, 0)}</Typography>
                                    </Box>
                                  )}
                                </Box>
                                <Box sx={{ width: '100%', height: 4, borderRadius: 999, bgcolor: alpha(verdict.meta.color, 0.12), overflow: 'hidden', mb: 0.35 }}>
                                  <Box sx={{ width: `${Math.min(100, Math.max(0, pillar.value || 0))}%`, height: '100%', bgcolor: verdict.meta.color, transition: 'width 0.3s' }} />
                                </Box>
                                {verdict.hint && (
                                  <Typography sx={{ fontSize: '0.66rem', color: '#64748b', lineHeight: 1.35 }}>
                                    {verdict.hint}
                                  </Typography>
                                )}
                              </Box>
                            );
                          })}
                          {detailDrawer.components.trap_penalty > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 0.6, mt: 0.3, borderTop: '1px dashed #fecaca' }}>
                              <Typography sx={{ fontSize: '0.7rem', color: '#b91c1c', fontWeight: 700 }}>⚠ {t('preparation.detail.pillars.trapPenalty')}</Typography>
                              <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#b91c1c', fontFamily: 'monospace' }}>−{fmtNum(detailDrawer.components.trap_penalty, 0)}</Typography>
                            </Box>
                          )}
                        </Stack>
                      </Box>
                    )}

                    {(detailDrawer.strength || detailDrawer.liquidity) && (
                      <Box sx={{ p: 1.5, border: '1px solid #e2e8f0', borderRadius: 1.25, bgcolor: '#fff' }}>
                        <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 }}>
                          {t('preparation.detail.strengthTitle')}
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
                          {detailDrawer.strength?.strength_score != null && (() => {
                            const v = getPillarVerdict('quality', detailDrawer.strength.strength_score);
                            return (
                              <Box>
                                <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>{t('preparation.detail.strengthScore')}</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.15 }}>
                                  <Box sx={{ px: 0.7, py: 0.1, borderRadius: 0.6, bgcolor: v.meta.bg, color: v.meta.color, fontSize: '0.66rem', fontWeight: 800 }}>{v.label}</Box>
                                  <Typography sx={{ fontSize: '0.66rem', color: '#94a3b8', fontFamily: 'monospace' }}>{fmtNum(detailDrawer.strength.strength_score, 0)}</Typography>
                                </Box>
                                {detailDrawer.strength.strength_tier_label && (
                                  <Typography sx={{ fontSize: '0.64rem', color: '#64748b', mt: 0.2 }}>
                                    {getLocalizedBreakoutLabel(detailDrawer.strength.strength_tier_label, i18n.resolvedLanguage)}
                                  </Typography>
                                )}
                              </Box>
                            );
                          })()}
                          {detailDrawer.strength?.relative_strength_score != null && (() => {
                            const v = getPillarVerdict('quality', detailDrawer.strength.relative_strength_score);
                            return (
                              <Box>
                                <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>{t('preparation.detail.relativeStrength')}</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.15 }}>
                                  <Box sx={{ px: 0.7, py: 0.1, borderRadius: 0.6, bgcolor: v.meta.bg, color: v.meta.color, fontSize: '0.66rem', fontWeight: 800 }}>{v.label}</Box>
                                  <Typography sx={{ fontSize: '0.66rem', color: '#94a3b8', fontFamily: 'monospace' }}>{fmtNum(detailDrawer.strength.relative_strength_score, 0)}</Typography>
                                </Box>
                                <Typography sx={{ fontSize: '0.64rem', color: '#64748b', mt: 0.2 }}>
                                  {detailDrawer.strength.relative_strength_score >= 70 ? t('preparation.detail.rsHints.leader') : detailDrawer.strength.relative_strength_score >= 55 ? t('preparation.detail.rsHints.outperform') : detailDrawer.strength.relative_strength_score >= 40 ? t('preparation.detail.rsHints.inline') : t('preparation.detail.rsHints.lagging')}
                                </Typography>
                              </Box>
                            );
                          })()}
                          {detailDrawer.liquidity?.avg_value_20d != null && (
                            <Box>
                              <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>{t('preparation.detail.avgValue20d')}</Typography>
                              <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#0f172a' }}>
                                {t('preparation.detail.billionVnd', { value: fmtNum(detailDrawer.liquidity.avg_value_20d / 1e9, 1) })}
                              </Typography>
                              <Typography sx={{ fontSize: '0.64rem', color: '#64748b' }}>
                                {detailDrawer.liquidity.avg_value_20d >= 50e9 ? t('preparation.detail.liqHints.high') : detailDrawer.liquidity.avg_value_20d >= 10e9 ? t('preparation.detail.liqHints.medium') : t('preparation.detail.liqHints.low')}
                              </Typography>
                            </Box>
                          )}
                          {detailDrawer.liquidity?.value_ratio != null && (
                            <Box>
                              <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>{t('preparation.detail.valueRatio')}</Typography>
                              <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: detailDrawer.liquidity.value_ratio >= 1.5 ? '#16a34a' : '#334155' }}>{fmtMultiplier(detailDrawer.liquidity.value_ratio, 2)}</Typography>
                              <Typography sx={{ fontSize: '0.64rem', color: '#64748b' }}>
                                {detailDrawer.liquidity.value_ratio >= 2 ? t('preparation.detail.volHints.surge') : detailDrawer.liquidity.value_ratio >= 1.3 ? t('preparation.detail.volHints.above') : detailDrawer.liquidity.value_ratio >= 0.8 ? t('preparation.detail.volHints.normal') : t('preparation.detail.volHints.below')}
                              </Typography>
                            </Box>
                          )}
                          {detailDrawer.strength?.leader_flag != null && (
                            <Box>
                              <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>{t('preparation.detail.leaderFlag')}</Typography>
                              <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: detailDrawer.strength.leader_flag ? '#16a34a' : '#94a3b8' }}>
                                {detailDrawer.strength.leader_flag ? t('preparation.detail.leaderYes') : t('preparation.detail.leaderNo')}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )}

                    <Box sx={{ p: 1.5, border: '1px solid #e2e8f0', borderRadius: 1.25, bgcolor: '#fff' }}>
                      <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 }}>
                        {t('preparation.detail.keyReasons')}
                      </Typography>
                      <Stack spacing={0.8}>
                        {(detailDrawer.reasons || []).length ? detailDrawer.reasons.map((reason, index) => (
                          <Box key={`${detailDrawer.ticker}-reason-${index}`} sx={{ display: 'flex', gap: 0.7, alignItems: 'flex-start' }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: index === 0 ? '#2563eb' : '#cbd5e1', mt: 0.55, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '0.74rem', color: '#334155', lineHeight: 1.4 }}>{localizeReason(reason)}</Typography>
                          </Box>
                        )) : (
                          <Typography sx={{ fontSize: '0.74rem', color: '#94a3b8' }}>{t('preparation.table.noReasons')}</Typography>
                        )}
                      </Stack>
                    </Box>
                  </>
                )}

                {detailDrawer.type === 'history' && (
                  <Box sx={{ p: 1.5, border: '1px solid #e2e8f0', borderRadius: 1.25, bgcolor: '#fff' }}>
                    <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 }}>
                      {t('preparation.detail.validationSnapshot')}
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
                      <Box>
                        <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>5D</Typography>
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: detailDrawer.fwd_ret_5d >= 0 ? '#16a34a' : '#dc2626' }}>{detailDrawer.fwd_ret_5d != null ? fmtPct(detailDrawer.fwd_ret_5d) : t('preparation.history.pending')}</Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>10D</Typography>
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: detailDrawer.fwd_ret_10d >= 0 ? '#16a34a' : '#dc2626' }}>{detailDrawer.fwd_ret_10d != null ? fmtPct(detailDrawer.fwd_ret_10d) : t('preparation.history.pending')}</Typography>
                      </Box>
                    </Box>
                    <Divider sx={{ my: 1.2 }} />
                    <Typography sx={{ fontSize: '0.68rem', color: '#64748b', lineHeight: 1.45 }}>
                      {t('preparation.detail.dayContext', {
                        total: detailDrawer.day_summary?.total_candidates || 0,
                        buy: detailDrawer.day_summary?.buy_count || 0,
                        reliability: fmtNum(detailDrawer.day_summary?.reliability_score, 1),
                      })}
                    </Typography>
                  </Box>
                )}
              </Stack>
            )}
          </Box>
          </Box>
        </Drawer>
  );
}
